import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

import _ from 'lodash'

import { env } from '@/env'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecord } from '@/shared/hooks'
import { useQuizzesProgresses } from '@/shared/hooks/useQuizzesProgresses'
import {
  LevelPlaySurfaceProvider,
  describeUnplayableActivities,
  findStepIndexById,
  useLevelPlay,
  useQuizAdapter
} from '@/shared/level-play'
import { api } from '@/shared/lib/api'
import { brandThemeVars } from '@/shared/lib/color/theme'
import { SiteMeta } from '@/shared/lib/meta/page-meta'
import { LoadWrapper } from '@/shared/ui'

import { getConfig } from './config'
import { getPlayability, getSteps } from './utils/getSteps'
import { restartQuizAttempt } from './utils/restartQuiz'

export const isDev = env.NODE_ENV == 'development'

const QuizContext = createContext(null)
const useQuizContext = () => useContext(QuizContext)

/**
 * Wrapper de domínio do quiz sobre o motor compartilhado.
 *
 * Este arquivo deixou de ser um segundo motor. O que sobra é o que é do quiz:
 * carregar o quiz e as perguntas (assíncronas, incrementais), montar o
 * `quizAdapter`, e traduzir a superfície genérica do motor para o vocabulário
 * que os 34 consumidores já usam (`onQuestionComplete`, `onQuizRestart`, …).
 *
 * **Índice único, base zero.** O quiz mantinha dois índices — `activeStepIndex`
 * base zero e `currentStepIndex` base um — que divergiam após qualquer
 * navegação. Agora existe um só, base zero.
 */
const QuizProvider = ({ digitalProductId, id, config, children, ...rest }) => {
  const { digitalProduct, eventSlug } = useDigitalProductContext()
  const { apiBasePath } = config

  const { record: quiz, loadWrapperConfig } = useRecord({ r: apiBasePath, id })

  // `quizzes` não tem `allow_wrong_answers` nem `block_redo` — a leitura era de
  // campos inexistentes com default fixo. O quiz sempre aceita erros (avança
  // após errar, custando vida quando `lives > 0`) e sempre permite refazer.
  const allowWrongAnswers = true
  const blockRedo = false
  const stageColor = _.get(quiz, 'stage.color')

  const { progress, rewards } = useQuizzesProgresses({ id: quiz?.id })

  const [questions, setQuestions] = useState([])
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true)
  const [isExhausted, setIsExhausted] = useState(false)

  const loadQuestions = useCallback(async () => {
    if (!id || !digitalProductId) return

    setIsLoadingQuestions(true)

    try {
      const response = await api.get(
        `/user/digital_products/${digitalProductId}/quiz_questions/next`,
        { params: { quiz_uid: id } }
      )
      const orderedQuestions = _.orderBy(
        response.data.records,
        'position',
        'asc'
      )

      setQuestions(orderedQuestions)
      // Lista vazia significa nada a jogar. Quem leva à tela de conclusão é o
      // motor, ao ver a fonte assíncrona esgotada — antes essa decisão estava
      // aqui dentro do carregamento.
      setIsExhausted(true)
    } finally {
      setIsLoadingQuestions(false)
    }
  }, [digitalProductId, id])

  useEffect(() => {
    loadQuestions()
  }, [loadQuestions])

  const steps = getSteps(questions)

  // Contabiliza as perguntas que a execução pulou — kind sem componente ou
  // conteúdo vazio. Sem isto elas sumiam sem rastro, e o sintoma chegava
  // distorcido: um quiz que não conclui. O aviso vai para o console, que o
  // painel de debug captura.
  // `questions` e recriado a cada render, entao o efeito dispara sempre: sem
  // esta trava o mesmo aviso sairia uma vez por render. Mesma protecao do lado
  // da jornada.
  const lastWarning = useRef(null)

  useEffect(() => {
    const resumo = describeUnplayableActivities(getPlayability(questions).unplayable)

    if (!resumo || resumo === lastWarning.current) return

    lastWarning.current = resumo
    console.warn(resumo)
  }, [questions])

  const adapter = useQuizAdapter({
    eventSlug,
    steps,
    isLoading: isLoadingQuestions,
    isExhausted,
    allowWrongAnswers,
    // `quizzes` não tem `max_attempts` — o limite real é `lives`, com `0`
    // significando ilimitadas (o servidor só encerra a tentativa por vidas
    // quando `lives > 0`, em `QuizAttempt#got_finished?`). `null` desliga a
    // mecânica: sem desconto, sem contador, sem tela de sem vidas.
    livesLimit: quiz?.lives > 0 ? quiz.lives : null,
    resolveQuestionId: useCallback(
      uid => questions.find(question => question.uid == uid)?.id,
      [questions]
    )
  })

  const engine = useLevelPlay({ adapter, resetKey: quiz?.id ?? null })

  const getQuestion = useCallback(
    questionId => questions.find(question => question.id == questionId),
    [questions]
  )

  const goToStepByQuestionId = useCallback(
    questionId => {
      const index = findStepIndexById(steps, questionId)

      if (index !== -1) engine.goToStepIndex(index)
    },
    [steps, engine]
  )

  const onQuizReset = useCallback(async () => {
    await api.post(`${apiBasePath}/${id}/reset`)
    engine.resetExecution()
    engine.goToFirstActivityStep()
  }, [apiBasePath, id, engine])

  /**
   * "Tentar mais uma vez" do quiz é tentativa nova: `quiz_questions/next`
   * devolve o conjunto da próxima tentativa (pool inteira com a anterior
   * encerrada; vazia com `repeat_chances` esgotado — e aí a fonte esgotada do
   * motor leva à conclusão). O conjunto de submetidos zera junto — ele pertence
   * à tentativa anterior. A re-busca é serializada antes de liberar o início:
   * sem ela o start roda sobre a lista velha e reenvia perguntas respondidas.
   * Falha na re-busca não prende o participante na tela de sem vidas.
   */
  const onQuizRestart = useCallback(
    () =>
      restartQuizAttempt({
        clearSubmitted: engine.clearSubmitted,
        loadQuestions,
        onRestart: engine.onRestart
      }),
    [engine, loadQuestions]
  )

  const goToFinishScreen = useCallback(async () => {
    engine.goToFinishScreen()
  }, [engine])

  const contextValue = {
    questionCardRef: engine.activityCardRef,
    countdownRef: engine.countdownRef,

    digitalProduct,
    quizId: id,
    quiz,
    rewards,
    progress,

    hearts: engine.hearts,
    loseHeart: engine.loseHeart,
    onLooseHeart: engine.loseHeart,

    isSubmitting: engine.isSubmitting,
    setIsSubmitting: engine.setIsSubmitting,
    disableControls: engine.disableControls,
    disableInputs: engine.disableInputs,
    answerIsWrong: engine.answerIsWrong,
    answerIsRight: engine.answerIsRight,
    answerIsEmpty: engine.answerIsEmpty,

    allowWrongAnswers,
    blockRedo,

    config,
    methods: engine.methods,

    onQuizReset,
    onQuizRestart,

    currentScreen: engine.currentScreen,
    goToFinishScreen,
    goToOutOfTimeScreen: engine.goToOutOfTimeScreen,
    goToOutOfHeartsScreen: engine.goToOutOfHeartsScreen,

    currentStepIndex: engine.currentStepIndex,
    steps: engine.steps,
    totalSteps: engine.totalSteps,
    stepsCustomFooters: engine.stepsCustomFooters,
    setStepsCustomFooters: engine.setStepsCustomFooters,
    goToPrevStep: engine.goToPrevStep,
    goToNextStep: engine.goToNextStep,
    goToStepByQuestionId,

    questions,
    getQuestion,
    onQuestionVerifyAnswer: engine.onVerifyAnswer,
    onQuestionComplete: engine.onComplete,
    onQuestionContinue: engine.onContinue,
    onQuestionAdvance: engine.onAdvance,
    onQuestionRetry: engine.onRetry,
    onQuestionStart: engine.goToFirstActivityStep,

    onAnswer: engine.onAnswer,

    startCountdown: engine.startCountdown,
    pauseCountdown: engine.pauseCountdown,

    loadWrapperConfig,

    ...rest
  }

  return (
    <LoadWrapper {...loadWrapperConfig} fullscreen>
      <SiteMeta
        defaultTitle={`${quiz?.name} - ${quiz?.journey?.name} | ${digitalProduct?.name}`}
        titleTemplate={`%s | ${digitalProduct?.name}`}
        faviconHref={_.get(
          digitalProduct,
          'favicon.file.url',
          '/placeholders/icon.png'
        )}
        description={quiz?.description}
        metaTags={[
          { name: 'viewport', content: 'width=device-width, user-scalable=no' }
        ]}
      />
      <QuizContext.Provider value={contextValue}>
        {/* Superfície de jogo (D4c): é o que os kinds consomem. O contexto de
            domínio acima segue existindo para shell e telas. */}
        {/* Sem caixa: a tela de quiz é a página, e a raiz de altura de viewport
            mora no `LevelPlay.Screen` que os filhos montam. `display: contents`
            mantém as custom properties de cor do stage herdando sem
            reintroduzir um box no meio da corrente de flex. */}
        <div
          className="contents"
          style={{
            // O stage sobrescreve a âncora localmente e a família inteira o
            // segue por herança de custom property — antes só três `--bs-*`
            // mudavam, derivados por mistura em sRGB e sem medir contraste.
            ...(stageColor ? brandThemeVars(stageColor) : {})
          }}
        >
          <LevelPlaySurfaceProvider
            surface={{
              onComplete: contextValue.onQuestionComplete,
              onContinue: contextValue.onQuestionContinue,
              onAnswer: contextValue.onAnswer,
              loseHeart: contextValue.loseHeart,
              setStepsCustomFooters: contextValue.setStepsCustomFooters,
              isSubmitting: contextValue.isSubmitting,
              disableInputs: contextValue.disableInputs,
              answerIsWrong: contextValue.answerIsWrong,
              answerIsRight: contextValue.answerIsRight
            }}
          >
            {children}
          </LevelPlaySurfaceProvider>
        </div>
      </QuizContext.Provider>
    </LoadWrapper>
  )
}

const withQuiz = (Component, props) => {
  const WrappedComponent = componentProps => {
    const config = getConfig(componentProps)

    return (
      <QuizProvider {...props} {...config} {...componentProps} config={config}>
        <Component {...componentProps} />
      </QuizProvider>
    )
  }

  WrappedComponent.displayName = `withQuiz(${
    Component.displayName || Component.name
  })`

  return WrappedComponent
}

export { QuizProvider, useQuizContext, withQuiz }
